import { AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ProductKey } from '../../lib/app-registry';
import { PRODUCT_FEATURE_LOSS } from '../../lib/product-features';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';

interface CancelPlanDialogProps {
  organizationId: number;
  productKey: ProductKey;
  productLabel: string;
}

/**
 * Cancel-plan confirmation with a feature-loss preview, opened from `SubscriptionBillingPanel`'s
 * "Cancel plan" button. Cancels at the end of the current billing term — never immediately.
 * Paid only: a trial is a no-card app entitlement with no Stripe subscription to cancel here.
 */
export function CancelPlanDialog({
  organizationId,
  productKey,
  productLabel,
}: CancelPlanDialogProps) {
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const features = PRODUCT_FEATURE_LOSS[productKey];

  const cancelMutation = trpc.billing.cancelSubscription.useMutation({
    onSuccess: () => {
      toast.success(`${productLabel} will cancel at the end of your current billing period.`);
      utils.workspace.getBillingState.invalidate({ productKey });
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || `Could not cancel ${productLabel}.`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="text-destructive hover:text-destructive">
          Cancel plan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel {productLabel}</DialogTitle>
          <DialogDescription>
            Your plan stays active until the end of your current billing period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Your team will lose access to these features
          </div>
          <ul className="space-y-2">
            {features.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Keep my plan
          </Button>
          <Button
            variant="destructive"
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate({ organizationId, productKey })}
          >
            {cancelMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="mr-2 h-4 w-4" />
            )}
            Cancel completely
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
